import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Globe, Brain, TrendingUp, Target, Share2, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { services, testimonials, stats, caseStudies } from '../data/mock';

export const Home = () => {
  const iconMap = {
    Globe,
    Brain,
    TrendingUp,
    Target,
    Share2
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 bg-gradient-to-br from-slate-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-in fade-in slide-in-from-left duration-700">
              <div className="inline-flex items-center space-x-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium">
                <Zap className="w-4 h-4" />
                <span>Agence Digitale 360°</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
                Transformez votre{' '}
                <span className="text-emerald-600 relative">
                  présence digitale
                  <svg 
                    className="absolute -bottom-2 left-0 w-full" 
                    height="12" 
                    viewBox="0 0 300 12"
                    fill="none"
                  >
                    <path 
                      d="M2 10C50 5 150 2 298 10" 
                      stroke="#059669" 
                      strokeWidth="3" 
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              </h1>

              <p className="text-xl text-slate-600 leading-relaxed">
                De la création de sites web à l'intelligence artificielle, nous développons 
                des solutions digitales qui génèrent des résultats concrets pour votre entreprise.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8 py-6 text-lg font-semibold transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl">
                    Démarrer Votre Projet
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="#services">
                  <Button variant="outline" className="border-2 border-slate-300 text-slate-700 hover:border-emerald-600 hover:text-emerald-600 rounded-full px-8 py-6 text-lg font-semibold transition-all duration-200">
                    Découvrir Nos Services
                  </Button>
                </a>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-8">
                {stats.map((stat) => (
                  <div key={stat.id} className="text-center">
                    <div className="text-3xl font-bold text-emerald-600">{stat.value}</div>
                    <div className="text-sm text-slate-600 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative animate-in fade-in slide-in-from-right duration-700 delay-200">
              <div className="relative z-10">
                <img 
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80" 
                  alt="Équipe collaborative"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-72 h-72 bg-emerald-200 rounded-2xl -z-0"></div>
              <div className="absolute -top-6 -left-6 w-48 h-48 bg-emerald-600 rounded-full opacity-10 -z-0"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section avec Photos */}
      <section id="services" className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Nos Services Digitaux
            </h2>
            <p className="text-xl text-slate-600">
              Une gamme complète de solutions pour propulser votre entreprise dans l'ère numérique
            </p>
          </div>

          {/* Service Cards with Images */}
          <div className="space-y-20">
            {/* Création de Sites Web */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="inline-flex items-center space-x-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium">
                  <Globe className="w-4 h-4" />
                  <span>Développement Web</span>
                </div>
                <h3 className="text-3xl sm:text-4xl font-bold text-slate-900">
                  Création de Sites Web
                </h3>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Des sites web sur-mesure qui convertissent vos visiteurs en clients. Sites vitrine, 
                  e-commerce, applications web personnalisées avec un design moderne et responsive.
                </p>
                <ul className="space-y-3">
                  {['Sites vitrine et corporate', 'E-commerce et marketplaces', 'Applications web personnalisées', 'Design responsive mobile-first'].map((item, idx) => (
                    <li key={idx} className="flex items-center space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                      <span className="text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/services/creation-sites-web">
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6 py-3">
                    Découvrir ce service
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src="https://images.unsplash.com/photo-1498050108023-c5249f4df085?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHx3ZWIlMjBkZXZlbG9wbWVudHxlbnwwfHx8fDE3NzQxNDY4ODh8MA&ixlib=rb-4.1.0&q=85" 
                  alt="Développement web" 
                  className="rounded-2xl shadow-lg col-span-2 h-64 object-cover"
                />
                <img 
                  src="https://images.unsplash.com/photo-1525547719571-a2d4ac8945e2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHxjb2RpbmclMjBsYXB0b3B8ZW58MHx8fHwxNzc0MjAyNTk1fDA&ixlib=rb-4.1.0&q=85" 
                  alt="Coding" 
                  className="rounded-2xl shadow-lg h-40 object-cover"
                />
                <img 
                  src="https://images.pexels.com/photos/6424589/pexels-photo-6424589.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" 
                  alt="Code" 
                  className="rounded-2xl shadow-lg h-40 object-cover"
                />
              </div>
            </div>

            {/* Intelligence Artificielle */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 grid grid-cols-2 gap-4">
                <img 
                  src="https://images.unsplash.com/photo-1674027444485-cec3da58eef4" 
                  alt="Intelligence Artificielle" 
                  className="rounded-2xl shadow-lg col-span-2 h-64 object-cover"
                />
                <img 
                  src="https://images.unsplash.com/photo-1697577418970-95d99b5a55cf" 
                  alt="AI Chip" 
                  className="rounded-2xl shadow-lg h-40 object-cover"
                />
                <img 
                  src="https://images.pexels.com/photos/5181148/pexels-photo-5181148.jpeg" 
                  alt="Robot" 
                  className="rounded-2xl shadow-lg h-40 object-cover"
                />
              </div>
              <div className="order-1 lg:order-2 space-y-6">
                <div className="inline-flex items-center space-x-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium">
                  <Brain className="w-4 h-4" />
                  <span>IA & Automatisation</span>
                </div>
                <h3 className="text-3xl sm:text-4xl font-bold text-slate-900">
                  Intelligence Artificielle
                </h3>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Intégrez l'IA pour automatiser et optimiser vos processus métier. Chatbots intelligents, 
                  analyse prédictive, traitement du langage naturel et plus encore.
                </p>
                <ul className="space-y-3">
                  {['Chatbots intelligents 24/7', 'Analyse de données et prédictions', 'Automatisation des processus', 'Intégration d\'APIs IA'].map((item, idx) => (
                    <li key={idx} className="flex items-center space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                      <span className="text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/services/intelligence-artificielle">
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6 py-3">
                    Découvrir ce service
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* SEO & Marketing Digital */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="inline-flex items-center space-x-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium">
                  <TrendingUp className="w-4 h-4" />
                  <span>SEO & Analytics</span>
                </div>
                <h3 className="text-3xl sm:text-4xl font-bold text-slate-900">
                  Référencement & Marketing Digital
                </h3>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Positionnez votre site en première page de Google. SEO, SEA, analytics et stratégie 
                  digitale pour maximiser votre visibilité et vos conversions.
                </p>
                <ul className="space-y-3">
                  {['Audit SEO complet', 'Campagnes Google Ads', 'Analyse de performance', 'Stratégie de contenu'].map((item, idx) => (
                    <li key={idx} className="flex items-center space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                      <span className="text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex gap-4">
                  <Link to="/services/referencement-seo">
                    <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6 py-3">
                      SEO
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <Link to="/services/publicite-sea">
                    <Button variant="outline" className="border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50 rounded-full px-6 py-3">
                      SEA
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src="https://images.unsplash.com/photo-1686061594225-3e92c0cd51b0" 
                  alt="Analytics Dashboard" 
                  className="rounded-2xl shadow-lg col-span-2 h-64 object-cover"
                />
                <img 
                  src="https://images.unsplash.com/photo-1599658880436-c61792e70672" 
                  alt="Analytics Work" 
                  className="rounded-2xl shadow-lg h-40 object-cover"
                />
                <img 
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71" 
                  alt="Performance" 
                  className="rounded-2xl shadow-lg h-40 object-cover"
                />
              </div>
            </div>

            {/* Réseaux Sociaux */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 grid grid-cols-2 gap-4">
                <img 
                  src="https://images.unsplash.com/photo-1724862936518-ae7fcfc052c1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxzb2NpYWwlMjBtZWRpYXxlbnwwfHx8fDE3NzQyMDI3MzF8MA&ixlib=rb-4.1.0&q=85" 
                  alt="Social Media" 
                  className="rounded-2xl shadow-lg col-span-2 h-64 object-cover"
                />
                <img 
                  src="https://images.unsplash.com/photo-1726066012749-f81bf4422d4e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxjb250ZW50JTIwY3JlYXRpb258ZW58MHx8fHwxNzc0MjAyNzM3fDA&ixlib=rb-4.1.0&q=85" 
                  alt="Content Creation" 
                  className="rounded-2xl shadow-lg h-40 object-cover"
                />
                <img 
                  src="https://images.pexels.com/photos/5361247/pexels-photo-5361247.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" 
                  alt="Social Branding" 
                  className="rounded-2xl shadow-lg h-40 object-cover"
                />
              </div>
              <div className="order-1 lg:order-2 space-y-6">
                <div className="inline-flex items-center space-x-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium">
                  <Share2 className="w-4 h-4" />
                  <span>Social Media</span>
                </div>
                <h3 className="text-3xl sm:text-4xl font-bold text-slate-900">
                  Gestion Réseaux Sociaux
                </h3>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Développez votre présence sociale et engagez votre communauté. Stratégie de contenu, 
                  création graphique, community management et campagnes d'influence.
                </p>
                <ul className="space-y-3">
                  {['Stratégie social media', 'Création de contenu visuel', 'Community management', 'Campagnes d\'influence'].map((item, idx) => (
                    <li key={idx} className="flex items-center space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                      <span className="text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/services/gestion-reseaux-sociaux">
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6 py-3">
                    Découvrir ce service
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section id="case-studies" className="py-20 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Nos Réalisations
            </h2>
            <p className="text-xl text-slate-600">
              Des projets qui parlent d'eux-mêmes : découvrez comment nous avons aidé nos clients à réussir
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {caseStudies.map((study) => (
              <Card 
                key={study.id}
                className="p-8 hover:shadow-xl transition-all duration-300 border-2 border-slate-100 rounded-2xl"
                style={{ backgroundColor: `${study.color}15` }}
              >
                <div className="space-y-4">
                  <div className="inline-block px-4 py-1 bg-white rounded-full text-sm font-medium" style={{ color: study.color }}>
                    {study.industry}
                  </div>
                  
                  <h3 className="text-2xl font-bold text-slate-900">{study.client}</h3>
                  
                  <p className="text-slate-600">{study.description}</p>
                  
                  <div className="pt-4 border-t border-slate-200">
                    <div className="text-sm text-slate-500 mb-1">Résultats</div>
                    <div className="text-xl font-bold" style={{ color: study.color }}>
                      {study.results}
                    </div>
                  </div>

                  <div className="text-sm text-slate-500">
                    Service: {study.service}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900">
                Pourquoi Choisir Enovation du Web ?
              </h2>
              <p className="text-xl text-slate-600 leading-relaxed">
                Nous combinons expertise technique, créativité et approche orientée résultats 
                pour faire de votre projet digital un succès.
              </p>

              <div className="space-y-4 pt-4">
                {[
                  'Équipe d\'experts certifiés et passionnés',
                  'Approche personnalisée pour chaque projet',
                  'Technologies de pointe et best practices',
                  'Suivi transparent et communication fluide',
                  'Support et maintenance continus',
                  'ROI mesurable et garanti'
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
                    <span className="text-slate-700 text-lg">{item}</span>
                  </div>
                ))}
              </div>

              <Link to="/contact" className="inline-block pt-4">
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8 py-6 text-lg font-semibold transition-all duration-200 transform hover:scale-105">
                  Discutons de Votre Projet
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80" 
                alt="Équipe professionnelle"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-emerald-600 to-emerald-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Ce Que Disent Nos Clients
            </h2>
            <p className="text-xl text-emerald-100">
              La satisfaction de nos clients est notre plus grande fierté
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card 
                key={testimonial.id}
                className="bg-white text-slate-900 p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 rounded-2xl"
              >
                <div className="space-y-4">
                  <div className="flex items-center space-x-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>
                  
                  <p className="text-slate-700 leading-relaxed italic">
                    "{testimonial.content}"
                  </p>

                  <div className="flex items-center space-x-3 pt-4 border-t border-slate-100">
                    <Avatar className="w-12 h-12 bg-emerald-600">
                      <AvatarFallback className="text-white font-semibold">
                        {testimonial.avatar}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="font-semibold text-slate-900">{testimonial.name}</div>
                      <div className="text-sm text-slate-500">{testimonial.company}</div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            Prêt à Booster Votre Croissance ?
          </h2>
          <p className="text-xl text-slate-300 mb-8 leading-relaxed">
            Discutons de votre projet et découvrez comment nous pouvons vous aider 
            à atteindre vos objectifs digitaux.
          </p>
          <Link to="/contact">
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-10 py-6 text-lg font-semibold transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl">
              Contactez-Nous Maintenant
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
